/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, ReactChild, useState } from "react";
import { useInView, IntersectionOptions } from "react-intersection-observer";
import getConfig from "next/config";
import {
    initSnowplowTracker,
    trackSnowplowClicks,
    trackSnowplowImpressions,
    triggerSnowplowPageViewEvent
} from "@themuse/snowplow-js-client";
import { useUserState } from "hooks/UserContext";
import { useAppState } from "hooks/AppContext";
import { isBrowser } from "./browser";
import { formatLog } from "./logging";

interface TrackedElementProps {
    children: ReactChild;
    eventName: string;
    eventProps?: Object;
    enabled?: boolean;
    callback?: () => void;
}

const getAppVersion = () => {
    const { publicRuntimeConfig: { appVersion } } = getConfig();
    return appVersion ? appVersion.split(".").slice(0, 2).join(".") : "";
};

interface BaseProps {
    app_version_id: string;
    page_referrer: string;
    query_string: string;
    page: string;
    path: string;
    experiments: any[];
    tm_source: string;
    status: number;
}

/**
 * Generate the base properties for any event. We added url data, experiment
 * data. And a few other things as well.
 *
 * @export
 * @returns {BaseProps}
 */
const genBaseProps = (): BaseProps => ({
    app_version_id: getAppVersion(),
    page_referrer: document?.referrer ?? "",
    query_string: window?.document?.location?.search ?? "",
    page: window?.document?.location?.pathname ?? "",
    path: window?.document?.location?.pathname ?? "",
    experiments: [],
    tm_source: "",
    status: 200
});

/**
 * Tracks a user
 *
 * @param {*} [userProps={}]
 */
const userTrack = (userProps: Object = {}) => {
    try {
        if (isBrowser) {
            // Track user
            console.info("Stub User Tracking Event", { ...userProps });
        }
    } catch (error) {
        console.error("Event error", JSON.stringify(formatLog({ error })));
    }
};

/**
 * TrackedElement: Wraps an element in an intersection observer with tracking props.
 */
const TrackedElement = (props: TrackedElementProps & IntersectionOptions) => {
    const {
        children, eventName, enabled = true, callback, ...restProps
    } = props;

    const intersectionProps: IntersectionOptions = {
        triggerOnce: true,
        rootMargin: "0px",
        ...restProps
    };
    const { ref, inView } = useInView(intersectionProps);

    useEffect(() => {
        if (inView && enabled) {
            if (callback) {
                callback();
            }
        }
    }, [inView]);

    return (
        <div ref={ref}>
            {children}
        </div>
    );
};

/**
 * Find a specific type of ancestor of this element.
 */
const findParentElement = (el: Element, elementTypes: string[]): Element | null => {
    if (elementTypes.includes(el.tagName)) {
        return el;
    }
    if (el.parentElement && el.parentElement !== document.body) {
        return findParentElement(el.parentElement, elementTypes);
    }
    return null;
};

const getTrackingPropsAsDataAttrs = (props: { clickEvent?: string; viewEvent?: string; eventProps?: any; }) => {
    const { clickEvent, viewEvent, eventProps } = props;
    const trackingProps: { [key: string]: string } = {};
    if (clickEvent) {
        trackingProps.oskarClick = clickEvent;
    }
    if (props.viewEvent) {
        trackingProps.oskarView = viewEvent;
    }
    trackingProps.oskarProps = JSON.stringify(eventProps || {});
    return trackingProps;
};

const pushDataLayerEvent = (event: string, props: { [key: string]: any } = {}) => {
    if (isBrowser() && window.dataLayer) {
        window.dataLayer.push({ event, ...props });
    }
};

/**
 * genJobTrackingProps: Generate the props for a tracked element.
 */
const genJobTrackingProps = (job: { hit: any; }, order: number) => JSON.stringify({
    company_id: job.hit.company.legacy_id,
    company_name: job.hit.company.name,
    company_short_name: job.hit.company.short_name,
    job_id: job.hit.legacy_id,
    job_level: job.hit.levels.map((level: { code: any; }) => level.code),
    job_locations: job.hit.locations.map((location: { address: any; }) => location.address),
    job_name: job.hit.title,
    job_short_name: job.hit.short_title,
    job_type: job.hit.apply_type,
    sp_impression_component: "tile",
    sp_impression_tile_type: "job",
    sp_impression_position: "main",
    sp_tile_type: "job",
    sp_tile_entity_id: job.hit.legacy_id.toString(),
    sp_tile_entity_uuid: job.hit.id,
    sp_tile_company_id: job.hit.company.legacy_id.toString(),
    sp_tile_location: "job_section",
    sp_tile_order: order + 1
});

/**
 * The SnowPlowInit function initializes the Snowplow tracker and tracks user interactions on a web page. It uses the
 * react-intersection-observer library to detect when a component is in view, and the @themuse/snowplow-js-client
 * library to send tracking events to a Snowplow collector. The function also sets up a useEffect hook to trigger a
 * page view event when the user is initialized.
 */
const SnowPlowInit = () => {
    const { publicRuntimeConfig: { snowplowCollectorUrl } } = getConfig();
    const user = useUserState();
    const { company } = useAppState();
    const [pageViewTriggered, setPageViewTriggered] = useState(false);

    const snowPlowPayload = {
        sp_page_is_external: false,
        sp_page_section: "company_profile",
        sp_page_type: "entity",
        sp_page_entity_id: company?.legacy_id?.toString() ?? null,
        sp_page_sponsor_id: company?.legacy_id?.toString() ?? null,
        sp_page_entity_uuid: company?.id ?? null,
    };

    React.useEffect(() => {
        if (!pageViewTriggered) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(snowPlowPayload);
            initSnowplowTracker(snowplowCollectorUrl);
            trackSnowplowClicks();
        }
    }, []);

    React.useEffect(() => {
        if (user.isInitialized && !pageViewTriggered) {
            triggerSnowplowPageViewEvent();
            setPageViewTriggered(true);
            trackSnowplowImpressions();
        }
    }, [user.isInitialized]);

    return null;
};

export {
    TrackedElement,
    getAppVersion,
    findParentElement,
    genBaseProps,
    userTrack,
    getTrackingPropsAsDataAttrs,
    pushDataLayerEvent,
    genJobTrackingProps,
    SnowPlowInit
};

export type {
    BaseProps,
    TrackedElementProps
};
